// The full compiled, `.bfrs`-scoped library stylesheet (design tokens + Tailwind
// base/components/utilities) as a string. Vite's `?inline` query runs the file
// through PostCSS/Tailwind and returns the resulting CSS instead of injecting a
// <link>, so we can adopt it into shadow roots that don't inherit document styles.
import compiledCss from "../styles/globals.css?inline";

let sharedSheet: CSSStyleSheet | null = null;
let constructableUnsupported = false;

/** The library CSS as a string — exposed for consumers building custom shadow hosts. */
export const bfrsShadowCss: string = compiledCss;

function getSharedSheet(): CSSStyleSheet | null {
  if (constructableUnsupported) return null;
  if (sharedSheet) return sharedSheet;

  if (
    typeof CSSStyleSheet === "undefined" ||
    typeof (CSSStyleSheet.prototype as { replaceSync?: unknown }).replaceSync !== "function"
  ) {
    constructableUnsupported = true;
    return null;
  }

  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(compiledCss);
    sharedSheet = sheet;
    return sheet;
  } catch {
    constructableUnsupported = true;
    return null;
  }
}

function appendStyleFallback(host: ShadowRoot | HTMLElement): void {
  if (host.querySelector("style[data-bfrs-shadow-style]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-bfrs-shadow-style", "");
  style.textContent = compiledCss;
  host.appendChild(style);
}

/**
 * Adopt the library stylesheet into a `ShadowRoot` or the `Document` (idempotent).
 * Uses a single shared constructable stylesheet when available, falling back to a
 * one-off `<style>` element otherwise.
 */
export function adoptBfrsStyles(target: ShadowRoot | Document): void {
  const sheet = getSharedSheet();

  if (sheet && "adoptedStyleSheets" in target) {
    if (!target.adoptedStyleSheets.includes(sheet)) {
      target.adoptedStyleSheets = [...target.adoptedStyleSheets, sheet];
    }
    return;
  }

  if (target instanceof Document) {
    if (target.head) appendStyleFallback(target.head as unknown as HTMLElement);
    return;
  }

  appendStyleFallback(target);
}

let documentAdopted = false;

/**
 * Ensure the library CSS is present in the main document. Overlays (Select,
 * Dropdown, Tooltip, Toast, …) portal to `<body>` outside any shadow root, so they
 * need the stylesheet at the document level to stay styled. The CSS is `.bfrs`
 * scoped, so it only affects `.bfrs` subtrees and cannot leak onto host markup.
 */
export function ensureDocumentStyles(): void {
  if (documentAdopted || typeof document === "undefined") return;
  documentAdopted = true;
  adoptBfrsStyles(document);
}
